import { useEffect, useState } from "react";
import {
  X,
  Upload,
  Loader2,
  Save,
  Image as ImageIcon,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

import { blogService, buildBlogFormData } from "@/services/blogService";
import RichTextEditor from "./RichTextEditor";

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/jpg", "image/webp"];

function initialForm(blog) {
  if (!blog) {
    return {
      title: "",
      category: "",
      shortDescription: "",
      authorName: "",
      date: "",
      readTime: "",
      tags: "",
      fullDescription: "",
      metaTitle: "",
      metaDescription: "",
      focusKeyword: "",
      metaKeywords: "",
      ogTitle: "",
      ogDescription: "",
      twitterTitle: "",
      twitterDescription: "",
      jsonSchema: "",
    };
  }

  const seo = blog.seo || {};
  return {
    title: blog.title || "",
    category: blog.category?._id || blog.category || "",
    shortDescription: blog.shortDescription || "",
    authorName: blog.authorName || "",
    date: blog.date ? String(blog.date).slice(0, 10) : "",
    readTime: blog.readTime ? String(blog.readTime) : "",
    tags: Array.isArray(blog.tags) ? blog.tags.join(", ") : "",
    fullDescription: blog.fullDescription || "",
    metaTitle: seo.metaTitle || "",
    metaDescription: seo.metaDescription || "",
    focusKeyword: seo.focusKeyword || "",
    metaKeywords: Array.isArray(seo.metaKeywords) ? seo.metaKeywords.join(", ") : "",
    ogTitle: seo.ogTitle || "",
    ogDescription: seo.ogDescription || "",
    twitterTitle: seo.twitterTitle || "",
    twitterDescription: seo.twitterDescription || "",
    jsonSchema: seo.jsonSchema ? JSON.stringify(seo.jsonSchema, null, 2) : "",
  };
}

// Editor khali hai ya nahi (<p><br></p> jaisa khali HTML bhi khali maana jayega)
function isEditorEmpty(html) {
  const text = String(html || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();
  return !text && !/<(img|iframe|video|table)/i.test(html || "");
}

// File se preview URL banata hai aur cleanup bhi karta hai
function useObjectUrl(file) {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    if (!file) {
      setUrl(null);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  return url;
}

export default function BlogForm({ blog, categories, onClose, onSaved, onOpenCategories }) {
  const isEdit = Boolean(blog);

  const [form, setForm] = useState(() => initialForm(blog));
  const [imageFile, setImageFile] = useState(null);
  const [ogFile, setOgFile] = useState(null);
  const [twFile, setTwFile] = useState(null);
  const [removeOg, setRemoveOg] = useState(false);
  const [removeTw, setRemoveTw] = useState(false);
  const [saving, setSaving] = useState(false);

  // ---------- image previews ----------
  // Backend og/twitter image nahi hone par blog image bhejta hai,
  // isliye "custom" tab maante hain jab URL blog image se alag ho.
  const existingMain = blog?.image || null;
  const existingOg = blog?.seo?.ogImage || null;
  const existingTw = blog?.seo?.twitterImage || null;
  const ogCustomExisting = Boolean(existingOg && existingOg !== existingMain);
  const twCustomExisting = Boolean(existingTw && existingTw !== existingMain);

  const imageBlob = useObjectUrl(imageFile);
  const ogBlob = useObjectUrl(ogFile);
  const twBlob = useObjectUrl(twFile);

  const mainPreview = imageBlob || existingMain;

  const ogIsCustom = Boolean(ogFile) || (ogCustomExisting && !removeOg);
  const ogPreview = ogFile ? ogBlob : ogIsCustom ? existingOg : mainPreview;

  const twIsCustom = Boolean(twFile) || (twCustomExisting && !removeTw);
  const twPreview = twFile ? twBlob : twIsCustom ? existingTw : mainPreview;

  // ---------- handlers ----------
  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function pickImage(e, setter, label, after) {
    const file = e.target.files?.[0];
    e.target.value = ""; // same file dobara choose ho sake
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error(`${label}: only JPG, PNG or WEBP allowed`);
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      toast.error(`${label} must be less than 5MB`);
      return;
    }
    setter(file);
    if (after) after();
  }

  function validateJSON(value) {
    if (!value.trim()) return true;
    try {
      JSON.parse(value);
      return true;
    } catch {
      toast.error("JSON-LD Schema contains invalid JSON");
      return false;
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!isEdit && !imageFile) return toast.error("Blog image is required");
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.category) return toast.error("Please select a category");
    if (!form.authorName.trim()) return toast.error("Author name is required");
    if (!form.shortDescription.trim()) return toast.error("Short description is required");
    if (isEditorEmpty(form.fullDescription)) return toast.error("Full description is required");
    if (form.readTime && (Number.isNaN(Number(form.readTime)) || Number(form.readTime) < 1)) {
      return toast.error("Read time must be a number (minutes)");
    }
    if (!validateJSON(form.jsonSchema)) return undefined;

    try {
      setSaving(true);

      const values = {
        title: form.title.trim(),
        category: form.category,
        shortDescription: form.shortDescription.trim(),
        authorName: form.authorName.trim(),
        fullDescription: form.fullDescription,
        tags: form.tags,
        metaTitle: form.metaTitle.trim(),
        metaDescription: form.metaDescription.trim(),
        focusKeyword: form.focusKeyword.trim(),
        metaKeywords: form.metaKeywords,
        ogTitle: form.ogTitle.trim(),
        ogDescription: form.ogDescription.trim(),
        twitterTitle: form.twitterTitle.trim(),
        twitterDescription: form.twitterDescription.trim(),
      };
      if (form.date) values.date = form.date;
      if (form.readTime) values.readTime = form.readTime;
      if (form.jsonSchema.trim()) values.jsonSchema = form.jsonSchema;
      else if (isEdit) values.jsonSchema = ""; // edit me clear karne ke liye

      const formData = buildBlogFormData(
        values,
        { image: imageFile, ogImage: ogFile, twitterImage: twFile },
        {
          removeOgImage: isEdit && removeOg,
          removeTwitterImage: isEdit && removeTw,
        }
      );

      const response = isEdit
        ? await blogService.update(blog._id, formData)
        : await blogService.create(formData);

      toast.success(
        response?.message || (isEdit ? "Blog updated successfully" : "Blog created successfully")
      );
      onSaved();
    } catch (error) {
      console.error("BLOG SAVE ERROR:", error);
      toast.error(error?.response?.data?.message || "Failed to save blog");
    } finally {
      setSaving(false);
    }
    return undefined;
  }

  // ---------- UI ----------
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-5">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => {
          if (!saving) onClose();
        }}
      />

      <div className="relative z-10 flex max-h-[96vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        {/* HEADER */}
        <div className="flex items-center justify-between gap-4 border-b border-slate-200 px-4 py-4 sm:px-5">
          <div className="min-w-0">
            <h2 className="font-heading text-xl font-bold text-slate-900 sm:text-2xl">
              {isEdit ? "Update Blog" : "Add Blog"}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Blog image, content and SEO settings.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* FORM */}
        <form onSubmit={handleSubmit} className="space-y-7 overflow-y-auto p-4 sm:p-6">
          {/* 1. BLOG IMAGE (sabse pehle) */}
          <Section title="Blog Image">
            <ImageUpload
              label="Blog Image"
              required={!isEdit}
              preview={mainPreview}
              fileName={imageFile?.name}
              onChange={(e) => pickImage(e, setImageFile, "Blog image")}
              note={
                isEdit
                  ? "Nayi image upload karoge to purani Cloudinary se delete ho jayegi."
                  : undefined
              }
            />
          </Section>

          {/* 2. BLOG DETAILS */}
          <Section title="Blog Details">
            <div className="space-y-4">
              <Field label="Title" required count={form.title.length} max={150}>
                <input
                  type="text"
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  maxLength={150}
                  placeholder="Enter blog title"
                  className="blog-input"
                />
              </Field>

              {isEdit ? (
                <Field label="Slug (auto-generated)">
                  <input type="text" value={blog.slug} disabled className="blog-input" />
                  <p className="mt-1 text-xs text-slate-400">
                    Title badalne par slug bhi naye title se ban jayega (purana URL kaam nahi karega).
                  </p>
                </Field>
              ) : (
                <InfoBox
                  icon={AlertCircle}
                  text="Slug title se automatically ban jayega. Example: 'Top 10 CET Tips' → top-10-cet-tips"
                />
              )}

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field label="Category" required>
                  <select
                    name="category"
                    value={form.category}
                    onChange={handleChange}
                    className="blog-input"
                  >
                    <option value="">Select Category</option>
                    {categories.map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  {categories.length === 0 && (
                    <p className="mt-1 text-xs text-amber-600">
                      Koi category nahi hai.{" "}
                      <button
                        type="button"
                        onClick={onOpenCategories}
                        className="font-semibold underline"
                      >
                        Pehle category banao
                      </button>
                    </p>
                  )}
                </Field>

                <Field label="Author Name" required>
                  <input
                    type="text"
                    name="authorName"
                    value={form.authorName}
                    onChange={handleChange}
                    placeholder="Author name"
                    className="blog-input"
                  />
                </Field>

                <Field label="Date">
                  <input
                    type="date"
                    name="date"
                    value={form.date}
                    onChange={handleChange}
                    className="blog-input"
                  />
                  <p className="mt-1 text-xs text-slate-400">Khali chhodo to aaj ki date lagegi.</p>
                </Field>

                <Field label="Read Time (minutes)">
                  <input
                    type="number"
                    min="1"
                    name="readTime"
                    value={form.readTime}
                    onChange={handleChange}
                    placeholder="Auto-calculated if empty"
                    className="blog-input"
                  />
                </Field>
              </div>

              <Field label="Tags">
                <input
                  type="text"
                  name="tags"
                  value={form.tags}
                  onChange={handleChange}
                  placeholder="cet, exam, career"
                  className="blog-input"
                />
                <p className="mt-1 text-xs text-slate-400">Separate tags with commas.</p>
              </Field>

              <Field label="Short Description" required count={form.shortDescription.length} max={300}>
                <textarea
                  name="shortDescription"
                  value={form.shortDescription}
                  onChange={handleChange}
                  maxLength={300}
                  rows={3}
                  placeholder="Card par dikhne wali chhoti description"
                  className="blog-input resize-none"
                />
              </Field>
            </div>
          </Section>

          {/* 3. FULL DESCRIPTION (rich text editor) */}
          <Section title="Full Description">
            <Field label="Content" required>
              <RichTextEditor
                value={form.fullDescription}
                onChange={(html) => setForm((prev) => ({ ...prev, fullDescription: html }))}
              />
              <p className="mt-1 text-xs text-slate-400">
                Visual tab me toolbar se likho, ya "HTML Code" tab me apna HTML code paste karo.
              </p>
            </Field>
          </Section>

          {/* 4. SEO */}
          <Section title="SEO">
            <div className="space-y-4">
              <Field label="Meta Title" count={form.metaTitle.length} max={70}>
                <input
                  type="text"
                  name="metaTitle"
                  value={form.metaTitle}
                  onChange={handleChange}
                  maxLength={70}
                  placeholder="SEO meta title"
                  className="blog-input"
                />
              </Field>

              <Field label="Meta Description" count={form.metaDescription.length} max={170}>
                <textarea
                  name="metaDescription"
                  value={form.metaDescription}
                  onChange={handleChange}
                  maxLength={170}
                  rows={3}
                  placeholder="SEO meta description"
                  className="blog-input resize-none"
                />
              </Field>

              <Field label="Focus Keyword">
                <input
                  type="text"
                  name="focusKeyword"
                  value={form.focusKeyword}
                  onChange={handleChange}
                  placeholder="cet preparation"
                  className="blog-input"
                />
              </Field>

              <Field label="Meta Keywords">
                <input
                  type="text"
                  name="metaKeywords"
                  value={form.metaKeywords}
                  onChange={handleChange}
                  placeholder="cet exam, cet preparation, corporate entrance test"
                  className="blog-input"
                />
                <p className="mt-1 text-xs text-slate-400">Separate keywords with commas.</p>
              </Field>
            </div>
          </Section>

          {/* OPEN GRAPH */}
          <Section title="Open Graph">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label="OG Title" count={form.ogTitle.length} max={70}>
                <input
                  type="text"
                  name="ogTitle"
                  value={form.ogTitle}
                  onChange={handleChange}
                  maxLength={70}
                  placeholder="Open Graph title"
                  className="blog-input"
                />
              </Field>

              <Field label="OG Description">
                <textarea
                  name="ogDescription"
                  value={form.ogDescription}
                  onChange={handleChange}
                  maxLength={200}
                  rows={3}
                  placeholder="Open Graph description"
                  className="blog-input resize-none"
                />
              </Field>
            </div>

            <ImageUpload
              label="OG Image"
              preview={ogPreview}
              fileName={ogFile?.name}
              onChange={(e) => pickImage(e, setOgFile, "OG image", () => setRemoveOg(false))}
              onRemove={
                ogIsCustom
                  ? () => {
                      setOgFile(null);
                      if (ogCustomExisting) setRemoveOg(true);
                    }
                  : undefined
              }
              note={
                ogIsCustom
                  ? "Custom OG image. X dabao to blog image use hogi."
                  : "Upload nahi karoge to blog image use hogi."
              }
            />
          </Section>

          {/* TWITTER */}
          <Section title="Twitter / X">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label="Twitter Title" count={form.twitterTitle.length} max={70}>
                <input
                  type="text"
                  name="twitterTitle"
                  value={form.twitterTitle}
                  onChange={handleChange}
                  maxLength={70}
                  placeholder="Twitter title"
                  className="blog-input"
                />
              </Field>

              <Field label="Twitter Description">
                <textarea
                  name="twitterDescription"
                  value={form.twitterDescription}
                  onChange={handleChange}
                  maxLength={200}
                  rows={3}
                  placeholder="Twitter description"
                  className="blog-input resize-none"
                />
              </Field>
            </div>

            <ImageUpload
              label="Twitter Image"
              preview={twPreview}
              fileName={twFile?.name}
              onChange={(e) => pickImage(e, setTwFile, "Twitter image", () => setRemoveTw(false))}
              onRemove={
                twIsCustom
                  ? () => {
                      setTwFile(null);
                      if (twCustomExisting) setRemoveTw(true);
                    }
                  : undefined
              }
              note={
                twIsCustom
                  ? "Custom Twitter image. X dabao to blog image use hogi."
                  : "Upload nahi karoge to blog image use hogi."
              }
            />
          </Section>

          {/* JSON-LD */}
          <Section title="Structured Data — JSON-LD">
            <InfoBox
              icon={AlertCircle}
              text="Valid JSON-LD dalo. Save se pehle JSON validate hota hai."
            />
            <Field label="JSON-LD Schema">
              <textarea
                name="jsonSchema"
                value={form.jsonSchema}
                onChange={handleChange}
                rows={10}
                spellCheck="false"
                placeholder={`{
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  "headline": "Blog title"
}`}
                className="blog-input resize-y font-mono text-xs leading-5"
              />
            </Field>
          </Section>

          {/* FOOTER */}
          <div className="flex flex-col-reverse gap-3 border-t border-slate-200 pt-5 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-lg border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#F5A623] px-5 py-2.5 text-sm font-bold text-slate-950 hover:bg-[#ffb638] disabled:opacity-50"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {isEdit ? "Update Blog" : "Save Blog"}
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        .blog-input {
          width: 100%;
          background: white;
          border: 1px solid #cbd5e1;
          border-radius: 0.5rem;
          padding: 0.65rem 0.75rem;
          color: #0f172a;
          outline: none;
          transition: 0.2s;
        }
        .blog-input:focus {
          border-color: #F5A623;
          box-shadow: 0 0 0 1px #F5A623;
        }
        .blog-input::placeholder { color: #94a3b8; }
        .blog-input:disabled {
          cursor: not-allowed;
          background: #f1f5f9;
          color: #64748b;
        }
      `}</style>
    </div>
  );
}

// ==========================================
// SMALL COMPONENTS
// ==========================================

function Section({ title, children }) {
  return (
    <section>
      <h3 className="mb-4 border-b border-slate-200 pb-2 text-base font-bold text-slate-900 sm:text-lg">
        {title}
      </h3>
      {children}
    </section>
  );
}

function Field({ label, required = false, count, max, children }) {
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-3">
        <label className="text-sm font-semibold text-slate-700">
          {label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
        {count !== undefined && (
          <span className="text-[11px] text-slate-400">
            {count}/{max}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

function InfoBox({ icon: Icon, text }) {
  return (
    <div className="mb-4 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
      <p className="text-xs leading-5 text-blue-700">{text}</p>
    </div>
  );
}

function ImageUpload({ label, required = false, preview, fileName, onChange, onRemove, note }) {
  return (
    <div className="mt-4 first:mt-0">
      <label className="mb-2 block text-sm font-semibold text-slate-700">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </label>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_240px]">
        {/* UPLOAD */}
        <label className="flex min-h-[170px] cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 px-4 text-center transition hover:border-[#F5A623] hover:bg-amber-50">
          <Upload className="mb-3 h-8 w-8 text-[#F5A623]" />
          <span className="max-w-full truncate text-sm text-slate-700">
            {fileName || "Click to upload image"}
          </span>
          <span className="mt-2 text-xs text-slate-400">JPG, PNG, WEBP • Max 5MB</span>
          <span className="mt-1 text-xs text-slate-500">Recommended: 1200 × 630</span>
          {note && <span className="mt-2 text-xs text-slate-500">{note}</span>}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={onChange}
            className="hidden"
          />
        </label>

        {/* PREVIEW */}
        <div className="relative flex min-h-[170px] items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
          {preview ? (
            <>
              <img
                src={preview}
                alt={`${label} preview`}
                className="h-full min-h-[170px] w-full object-cover"
              />
              {onRemove && (
                <button
                  type="button"
                  onClick={onRemove}
                  className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-red-600 text-white shadow-lg transition hover:bg-red-700"
                  title={`Remove ${label}`}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </>
          ) : (
            <div className="px-4 text-center text-slate-400">
              <ImageIcon className="mx-auto mb-2 h-8 w-8" />
              <p className="text-xs">No image selected</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}